/* ============================================================================
   SERVICES — concrete implementations registered into the ServiceManager.
   v1 is the infrastructure tier (config, auth, mock store, presence, scope).
   Each extends the framework BaseService: the constructor receives a single
   ServiceActivationContext (name, priority, config, manager, scheduler, ...),
   config is read via this.serviceConfig, and lifecycle hooks (initialize/start)
   are overridden as needed. Domain services (orgs, payouts, analytics, ...) are
   the next layer and implement the contracts in src/contracts (plan M3).
   ============================================================================ */
import { BaseService } from '../runtime';
import type { ServiceActivationContext } from '../runtime';
import type { AppConfig } from '../config';
import type {
  IConfigService,
  IAuthSessionService,
  IMockStoreService,
  IPresenceService,
  IScopeService,
  Principal,
  PresenceSnapshot,
  Scope,
} from '../contracts';

/** Tier-1: configuration / feature flags / endpoints. */
export class ConfigService extends BaseService<AppConfig> implements IConfigService {
  get mode(): AppConfig['mode'] {
    return this.serviceConfig.mode;
  }
  get env(): string {
    return this.serviceConfig.env;
  }
  get bffPath(): string {
    return this.serviceConfig.bffPath;
  }
  isMock(): boolean {
    return this.serviceConfig.mode === 'mock';
  }
}

/** Tier-1: owns the bundled mock store (ported window.DB tables + window.Q selectors). */
export class MockStoreService extends BaseService implements IMockStoreService {
  get q(): Record<string, any> {
    return window.Q;
  }
  get db(): Record<string, any> {
    return window.DB;
  }
  override initialize(): void {
    if (!window.Q || !window.DB) {
      throw new Error('MockStoreService: design data layer (window.DB / window.Q) must load before boot');
    }
  }
}

/**
 * Tier-1: session identity. In 'mock' mode it is driven by the prototype's demo
 * sign-in; in 'hybrid' it would be seeded from the Cloudflare Access identity
 * resolved by the BFF (email → principal). Same interface either way.
 */
export class AuthSessionService extends BaseService implements IAuthSessionService {
  private principal: Principal | null = null;

  current(): Principal | null {
    return this.principal;
  }
  signIn(userId: string): Principal {
    const u = window.Q.user(userId);
    this.principal = { userId: u.id, email: u.email, name: u.name, role: u.role };
    return this.principal;
  }
  signOut(): void {
    this.principal = null;
  }
}

/** Tier-2: live presence. REAL data exists (app_meta) — mock until the BFF is wired. */
export class PresenceService extends BaseService implements IPresenceService {
  snapshot(): PresenceSnapshot {
    const sessions = (window.DB?.sessions ?? []).length as number;
    return { online: Math.max(12, Math.round(sessions * 0.04)), activeMeditations: 3, source: 'mock' };
  }
}

/** Tier-2: turns a principal + membership into a Scope via the ported scope engine. */
export class ScopeService extends BaseService implements IScopeService {
  private readonly store: IMockStoreService;

  constructor(context: ServiceActivationContext, store: IMockStoreService) {
    super(context);
    this.store = store;
  }

  build(userId: string, membershipId?: string | null): Scope {
    return this.store.q.scopeFor(userId, membershipId ?? null) as Scope;
  }
}
